import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ToastContainer from '@/components/ui/ToastContainer';

type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
    id: string;
    type: ToastType;
    message: string;
    duration: number;
}

interface ToastContextValue {
    showToast: (type: ToastType, message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const TOAST_DURATION = 2800;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const queueRef = React.useRef<ToastItem[]>([]);
    const [activeToast, setActiveToast] = useState<ToastItem | null>(null);

    const advanceToast = useCallback(() => {
        const [nextToast, ...rest] = queueRef.current;
        queueRef.current = rest;
        setActiveToast(nextToast ?? null);
    }, []);

    const removeToast = useCallback(() => {
        advanceToast();
    }, [advanceToast]);

    const showToast = useCallback<ToastContextValue['showToast']>(
        (type, message, duration = TOAST_DURATION) => {
            const id = `${Date.now()}-${Math.random()}`;
            const nextToast = { id, type, message, duration };

            setActiveToast((current) => {
                if (current === null && queueRef.current.length === 0) {
                    return nextToast;
                }
                queueRef.current = [...queueRef.current, nextToast];
                return current;
            });
        },
        []
    );

    useEffect(() => {
        if (!activeToast) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            advanceToast();
        }, activeToast.duration);

        return () => window.clearTimeout(timer);
    }, [activeToast, advanceToast]);

    const value = useMemo(() => ({ showToast }), [showToast]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer toast={activeToast} onClose={removeToast} />
        </ToastContext.Provider>
    );
};

export const useToast = (): ToastContextValue => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};
